import axios from "axios";
import useSWR, { mutate } from "swr";
import useSWRImmutable from "swr/immutable";
import { cartFetcher, config, API_URL, fetcher } from "./apiConfig";
import { uniq } from "lodash";
import { SESSION_NAME as CHECKOUT_SESSION } from "../pages/Checkout/CheckoutSession";
import WOO_ERRORS from "../Helpers/woocommerceErrors";

const baseUrl = "/wp-json/wc/store";

const storeFetcher = (url) => axios.get(url, config).then((res) => res.data);

/**
 * Retrieves complementary products category based on slug of category
 * @param {string} slug
 * @returns array of products | []
 */
export const getComplementaryItems = (slug) => {
    if (!pottsalatProducts && !(slug in pottsalatProducts)) {
        return [];
    }
    return pottsalatProducts[slug];
};

/**
 * Fetches Cart
 * @returns Cart
 */
export const getCart = () => {
    const { data, error } = useSWRImmutable(`${baseUrl}/cart/`, cartFetcher, {
        onErrorRetry: (error, _key, _config, revalidate, { retryCount }) => {
            if (error.status === 404 || retryCount >= 5) {
                return;
            }

            setTimeout(
                () =>
                    revalidate({
                        retryCount,
                    }),
                5000
            );
        },
    });

    return {
        cart: data,
        cartLoading: !error && !data,
        isError: error,
        requiresPayment: (data && data.needs_payment) || false,
        cartErrors: (data && data.errors) || [],
    };
};

export const getProduct = (productId, franchiseId, storeId) => {
    const queryString = `${API_URL}/products/${productId}`;
    const params = new URLSearchParams({ franchise_id: franchiseId });
    if (storeId != undefined) {
        params.append("store_id", storeId);
    }
    const shouldFetch = !!productId && !!franchiseId;

    const { data, error } = useSWR(
        shouldFetch ? queryString + "?" + params.toString() : undefined,
        storeFetcher
    );

    return {
        product: data,
        isLoading: !error && !data,
        isError: !!error,
    };
};

export const fetchCartItems = async () => {
    try {
        const response = await axios.get(`${baseUrl}/cart/items`);
        return { ok: true, data: response.data };
    } catch (e) {
        return { ok: false, data: [] };
    }
};

/**
 * Programmatically enforce a referesh to WC cart
 * @param data (Data should be a complete WC cart response or empty to refetch from the cart API)
 *
 * @usage   Updating something while on cart / checkout page
 *          which inpacts other sets of data eg. discounts, totals, address
 *          This function revalidates the state on the frontend and retrives
 *          a correct cart response and hydrates the react application
 *
 * @example @see AddItemToCart
 *          adds an item and gets a complete cart as a response
 *          in this case no need to actually query for cart as we
 *          we can already update the app cache with the reponse we retrive
 * @example @see updateCompensation
 *          does not retrive the complete cart response so we query the
 *          cart API to retrive up to date data of the cart
 */
export const refreshCart = async (data) => {
    if (data) {
        // First update to the returned data ASAP and after revalidate (To fetch possible Firm discount)
        mutate(`${baseUrl}/cart/`, data, false);
        // mutate(`${baseUrl}/cart/`); // We do not use firm discount on release-1 so this could be excluded
    } else {
        await mutate(`${baseUrl}/cart/`);
    }
};

/**
 * Adds a new standalone item to the cart
 * @param {number} key
 * @returns
 */
export const AddItemToCart = async (key, quantity = 1) => {
    const data = { id: key, quantity: quantity };

    try {
        const response = await axios.post(
            `${baseUrl}/cart/add-item`,
            data,
            config
        );
        refreshCart(response.data);
        return { ok: response.status === 201 };
    } catch (error) {
        return { ok: false, msg: error.response.data.message };
    }
};

/**
 * Adds a product (salads, bowls) from products details page to the cart
 * @param {object} ingredients
 * @param {number} key
 * @param {number} quantity
 */
export const AddProductToCart = async ({
    ingredients,
    key,
    quantity = 1,
    comment = null,
    isMainDish = false,
    removedIngredient,
}) => {
    // Adds a standalone product to cart (drinks, desserts, other)
    if (!isMainDish) {
        return await AddItemToCart(key, quantity);
    }
    try {
        const data = {
            ingredients,
            product_id: key,
            method: "addon",
            quantity: quantity,
        };

        if (comment !== null) data.comment = comment;
        if (removedIngredient !== null)
            data.remove_ingredient = removedIngredient;

        const response = await axios.post(
            `/?wc-ajax=pottsalat_add_to_cart`,
            data,
            config
        );
        updateLocation();
        const { status, redirect } = response.data;
        return { ok: status === 0, redirect: redirect };
    } catch {
        return { ok: false };
    }
};

/**
 * Removes a products from the cart
 * @param {number[]} items
 * @returns
 */
export const RemoveMultipleItemsFromCart = async (items) => {
    try {
        for (const idx in items) {
            let key = items[idx];
            const response = await RemoveItemFromCart(key, false);
            if (response.ok) refreshCart(response.data);
        }
        return {
            ok: true,
            message: "Cart is up to date with store stocks",
        };
    } catch (error) {
        return { ok: false, message: error.message };
    }
};

export const addMultipleItemsToCart = async (products) => {
    try {
        const data = products.map((p) => {
            return {
                quantity: p.count,
                ingredients: p.extensions.pottsalat.ingredients,
                product_id: p.id,
                method: p.method,
            };
        });

        const response = await axios.post(
            "/?wc-ajax=pottsalat_add_multiple_to_cart",
            {
                products: data,
            },
            config
        );

        return { ok: response.data.status === 0 };
    } catch (err) {
        return { ok: false };
    }
};

/**
 * Removes a product from the cart
 * @param {number} key
 * @returns
 */
export const RemoveItemFromCart = async (key, refetchCart = true) => {
    const data = { key };

    try {
        const response = await axios.post(
            `${baseUrl}/cart/remove-item`,
            data,
            config
        );
        if (refetchCart) refreshCart(response.data);
        return { ok: response.status === 200, data: response.data };
    } catch (error) {
        return { ok: false, data: [] };
    }
};

/**
 * Updates products quantity in cart
 * @param {number} key
 * @param {number} quantity
 * @returns
 */
export const UpdateItemFromCart = async (key, quantity) => {
    const data = {
        key,
        quantity,
    };

    try {
        const response = await axios.post(
            `${baseUrl}/cart/update-item`,
            data,
            config
        );
        refreshCart(response.data);
        return response.status === 200;
    } catch (error) {
        return false;
    }
};

/**
 * Applies WC coupon
 * @param {string} code
 * @returns
 */
export const ApplyCoupon = async (code) => {
    const data = { code };
    try {
        const response = await axios.post(
            `${baseUrl}/cart/apply-coupon/`,
            data,
            config
        );
        refreshCart(response.data);
        return { ok: response.status === 200 };
    } catch (err) {
        return { ok: false, msg: err.response.data.message };
    }
};

/**
 * Removes WC coupon
 * @param {string} code
 * @returns
 */
export const RemoveCoupon = async (code) => {
    const data = { code };
    try {
        const response = await axios.post(
            `${baseUrl}/cart/remove-coupon/`,
            data,
            config
        );
        refreshCart(response.data);
        return response.status === 200;
    } catch (error) {
        return false;
    }
};

/**
 * Updates users compensation for CO2 and water on checkout
 *
 * @param {"none" | "single" | "double"} compensation
 * @returns bool
 */
export const updateCompensation = async (compensation) => {
    const formData = new FormData();
    formData.append("compensation", compensation);
    try {
        const response = await axios.post(
            "/warenkorb/?wc-ajax=compensate",
            formData,
            {
                headers: { "Content-Type": "multipart/form-data" },
            }
        );
        await refreshCart();
        return response.status === 200 && response.data !== "nope";
    } catch (err) {
        return false;
    }
};

/**
 * Updates users contact details on checkout
 *
 * Values are : {sameAsBilling, deliveryAddress, billingAddress, phone, email}
 *
 * @param {object} values
 * @returns response
 */
export const updateCustomer = async (values) => {
    const { sameAsBilling, deliveryAddress, phone, email } = values;

    let billingAddress;
    if (sameAsBilling) {
        billingAddress = deliveryAddress;
    } else {
        billingAddress = values.billingAddress;
    }

    const data = {
        billing_address: { ...billingAddress, phone, email },
        shipping_address: deliveryAddress,
    };

    try {
        const response = await axios.post(
            `${baseUrl}/cart/update-customer`,
            data,
            config
        );
        refreshCart(response.data);
        const { billing_address, shipping_address } = response.data;
        return {
            ok: response.status === 200,
            data: { billing_address, shipping_address, sameAsBilling },
        };
    } catch (err) {
        if (!err.response.data) {
            return { ok: false, msg: "" };
        }
        const { params } = err.response.data;
        const msg = uniq(Object.values(params)).join(", ");
        return { ok: false, msg };
    }
};

/**
 * Updates area location for delivery
 * Replacement for previous jquery request from DeliveryRoot
 *
 * @returns bool
 */
export const updateLocation = async () => {
    try {
        let cookie = window.cookie.get();
        let data = {};
        if (cookie.address) {
            data.address =
                cookie.address.address && cookie.address.housenumber
                    ? cookie.address.address + " " + cookie.address.housenumber
                    : null;
            data.postcode = cookie.address.postcode;
            data.city = cookie.address.locality;
        }
        if (cookie.area) data.dispatch_type = cookie.area.type;
        return true; // Doesnt require a response and async
    } catch {
        return false;
    }
};

/**
 * Adds a configuration to the shopping cart
 * @param ingredients [{amount: number, ingredient: {...}}, ...]
 * @param productType salad | bowl
 * @returns {Promise<boolean>}
 */
export const addConfigurationToCart = (ingredients, productType, baseID) => {
    let products = ingredients
        .filter((ing) => ing.amount > 0)
        .map((ing) => ({ id: ing.ingredient.id, count: ing.amount }));
    const data = {
        ingredients: products,
        quantity: 1,
        method: "config",
        product_type: productType,
        product_id: baseID,
    };

    return new Promise((resolve, reject) => {
        axios
            .post(`/?wc-ajax=pottsalat_add_to_cart`, data, config)
            .then((response) => {
                updateLocation();
                if (`${response.status}`.startsWith("2")) {
                    return resolve(response.data);
                } else {
                    return reject(response.statusText);
                }
            })
            .catch(reject);
    });
};

/**
 * Returns number of items in cart
 * @returns {number} count of items in cart
 */
export const getTotalCartNumber = () => {
    for (const [key, value] of Object.entries(window.sessionStorage)) {
        if (key.includes("wc_fragments")) {
            const parsedWC_cartInfo = JSON.parse(value);
            const countInfo = parsedWC_cartInfo["a.cart-contents"];
            const number = countInfo.match(/(\d+)/);
            if (number) return number[0];
            return 0;
        }
    }

    return 0;
};

export const clearCheckoutSession = () =>
    sessionStorage.removeItem(CHECKOUT_SESSION);

/**
 * @example Submits order to the API and redirects
 *  A) No payment needed -> to thank you page
 *  B) Payment needed -> to payment page
 *
 * @param {Object} orderData
 * @returns
 */
export const submitOrder = async (orderData) => {
    try {
        const order = formatOrderData(orderData);

        const response = await axios.post(
            `${baseUrl}/checkout?_locale=user`,
            order,
            config
        );

        if (response.status !== 200) {
            throw new Error(response);
        }

        const { payment_details } = response.data.payment_result;

        // No payment needed for this order -> redirect to order thank you page
        if (payment_details.length === 0) {
            return {
                ok: true,
                redirect: response.data.payment_result.redirect_url,
            };
        }

        const secondPayload = payment_details.find(
            (paymentDetail) => paymentDetail.key === "body"
        );

        if (!secondPayload) {
            throw new Error("No payment details found");
        }

        const url = `${API_URL.substr(0, API_URL.length - 16)}`;

        const paymentResponse = await axios.post(
            `${url}/api/v1/shop/orders`,
            { data: secondPayload.value },
            config
        );

        return { ok: true, redirect: paymentResponse.data.redirect_to };
    } catch (err) {
        if (!err.response) {
            return { ok: false, redirect: "" };
        }

        const { data } = err.response;

        const isCouponError = data.code === WOO_ERRORS.COUPON_ERROR;

        if (isCouponError) {
            const { cart } = data.data;
            await refreshCart(cart);
            return {
                ok: false,
                redirect: "",
                error: data.message,
            };
        }

        return { ok: false, redirect: "" };
    }
};

/**
 * Creates a order DTO that matches WC Api
 *
 * @param {Object} data
 * @returns
 */
const formatOrderData = (data) => {
    const { cookie, contextState, requiresPayment, comment } = data;
    const { orderAddress, earlyOnLocation, timeSlot, cutlery, paymentOption } =
        contextState;

    return {
        billing_address: orderAddress.billing_address,
        customer_note: comment,
        extensions: {
            pottsalat: {
                area_id: cookie.area.id,
                store_id: cookie.area.store_id,
                ...(cookie.area.type === "delivery" && {
                    contactless_delivery:
                        data.deliveryInstructions?.contactlessDelivery || false,
                    no_doorbell:
                        data.deliveryInstructions?.no_doorbell || false,
                    delivery_phone_call:
                        data.deliveryInstructions?.delivery_phone_call || false,
                    give_porter:
                        data.deliveryInstructions?.give_porter || false,
                }),
                cutlery: cutlery,
                ...(cookie.area.type === "delivery" && {
                    earlier_allowed: earlyOnLocation,
                }),
                dispatch_type: cookie.area.type,
                ...(cookie.area.type === "delivery" && {
                    lat: String(cookie.location.lat),
                }),
                ...(cookie.area.type === "delivery" && {
                    lon: String(cookie.location.lon),
                }),
                slot_begin: timeSlot.begin,
                slot_end: timeSlot.end,
                slot_id: timeSlot.id,
            },
        },
        ...(requiresPayment
            ? {
                  payment_data: [
                      {
                          key: "wc-pottsalat-new-payment-method",
                          value: false,
                      },
                  ],
              }
            : {}),
        ...(requiresPayment ? { payment_method: paymentOption.value } : {}),
        shipping_address: {
            ...orderAddress.shipping_address,
            phone: orderAddress.billing_address.phone,
        },
        should_create_account: false,
    };
};

/**
 * @param {area_update, location_update, address_update} data
 */
export const dispatchLocationChange = (data) => {
    window.cookie.update(data);
    window.dispatchEvent(
        new CustomEvent("cookie-update", {
            bubbles: false,
            detail: data,
        })
    );
};

/**
 * Fetch store data
 *
 * @param {string | null} storeId
 * @returns
 */
export const fetchStoreData = (storeId) => {
    const url = `${reactConfig.url}/stores`;
    const { data, error } = useSWRImmutable(url, storeFetcher);

    const isLoading = !error && !data;
    const isError = error;
    const store = data?.find((store) => store.id === storeId);

    const getStoreAddress = () => {
        if (!store) {
            return "";
        }

        return `${store.address}, ${store.postcode} ${store.locality}`;
    };

    return {
        store,
        isLoading,
        isError,
        greenClubEnabled: Boolean(store?.membership_enabled),
        franchiseId: store?.franchise_id,
        storePickupAddress: getStoreAddress(),
    };
};

export const usePickupAreas = () => {
    const url = `${reactConfig.url}/pickup-areas`;

    const { data, error } = useSWRImmutable(url, storeFetcher);

    const isLoading = !error && !data;
    const isError = error;

    return {
        pickupAreas: isLoading || isError ? [] : data,
        isLoading,
        isError,
    };
};

export const useQueryPostcodes = (zip) => {
    const url = `${reactConfig.url}/postcodes/by-postcode`;
    const isValidZip = zip.length === 5 && new RegExp("^[0-9]+$").test(zip);

    const { data, error } = useSWRImmutable(
        () => (isValidZip ? `${url}/${zip}` : null),
        fetcher,
        {
            shouldRetryOnError: false,
        }
    );

    const isLoading = !error && !data;
    const isError = error;

    return {
        postcodeLocation: isLoading || isError ? null : data,
        isLoading,
        isError,
    };
};
